import React, { useCallback, useEffect, useState } from "react";
import {
  DEFAULT_AGENT_MODEL,
  DEFAULT_AGENT_REASONING_EFFORT,
  DEFAULT_ENGINEER_LEVEL,
  AGENT_MODEL_OPTIONS,
  AGENT_REASONING_EFFORT_OPTIONS,
  ENGINEER_LEVELS,
  PROFILE_TYPE_AI_AGENT,
  PROFILE_TYPE_HUMAN
} from "../domainCatalog";
import "./TeamStudioPage.css";


const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const EMPTY_MEMBER = {
  name: "",
  job_title: "",
  github_username: "",
  level: DEFAULT_ENGINEER_LEVEL,
  main_skills: [],
  availability_hours_per_week: 40,
  photo_data_url: null,
  profile_type: PROFILE_TYPE_HUMAN,
  agent_model: DEFAULT_AGENT_MODEL,
  agent_reasoning_effort: DEFAULT_AGENT_REASONING_EFFORT,
  agent_max_cost_per_run: 5,
  agent_max_concurrent_jobs: 1,
  agent_instructions: ""
};

const TeamStudioPage = ({ token }) => {
  const [members, setMembers] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [draft, setDraft] = useState(EMPTY_MEMBER);
  const [savedDraft, setSavedDraft] = useState(EMPTY_MEMBER);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const isNew = selectedId === null;
  const hasChanges = JSON.stringify(draft) !== JSON.stringify(savedDraft);

  const loadTeam = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/team`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed to load team.");
      setMembers(data.members);
    } catch (error) {
      setMessage(error.message || "Error loading team.");
    }
  }, [token]);

  useEffect(() => {
    loadTeam();
  }, [loadTeam]);

  const startNew = () => {
    setSelectedId(null);
    setDraft({ ...EMPTY_MEMBER });
    setSavedDraft({ ...EMPTY_MEMBER });
    setMessage("");
  };

  const selectMember = (member) => {
    const next = toDraft(member);
    setSelectedId(member.id);
    setDraft(next);
    setSavedDraft(next);
    setMessage("");
  };

  const saveMember = async () => {
    if (!draft.name.trim()) {
      setMessage("Name is required.");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/team${isNew ? "" : `/${selectedId}`}`, {
        method: isNew ? "POST" : "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          ...draft,
          name: draft.name.trim(),
          main_skills: normalizeSkills(draft.main_skills)
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed to save team member.");
      setMembers(data.members);
      const saved = isNew
        ? data.members.find((member) => member.name === draft.name.trim()) || data.members[data.members.length - 1]
        : data.members.find((member) => member.id === selectedId);
      if (saved) selectMember(saved);
      setMessage("Team member saved.");
    } catch (error) {
      setMessage(error.message || "Error saving team member.");
    } finally {
      setLoading(false);
    }
  };

  const deleteMember = async () => {
    const selected = members.find((member) => member.id === selectedId);
    if (!selected || !window.confirm(`Delete ${selected.name}? Their assigned tickets will become unassigned.`)) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/team/${selected.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed to delete team member.");
      setMembers(data.members);
      startNew();
      setMessage("Team member deleted. Their tickets are now unassigned.");
    } catch (error) {
      setMessage(error.message || "Error deleting team member.");
    } finally {
      setLoading(false);
    }
  };

  const uploadPhoto = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      setMessage("Photo must be smaller than 2 MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => updateDraft(setDraft, "photo_data_url", reader.result);
    reader.readAsDataURL(file);
  };

  return (
    <div className="team-studio-page">
      <header className="team-studio-header">
        <div>
          <h1>Team Studio</h1>
          <p>Maintain the workspace roster and make engineers available across all workstreams.</p>
        </div>
        <button onClick={startNew}>New profile</button>
      </header>
      {message && <div className="team-studio-message">{message}</div>}
      <div className="team-studio-workspace">
        <section className="team-roster">
          <div className="team-panel-title"><strong>Workspace team</strong><span>{members.length} members</span></div>
          {!members.length && <p className="team-empty">Add the first team member to begin assigning work.</p>}
          {members.map((member) => (
            <button
              key={member.id}
              className={`team-member-card ${selectedId === member.id ? "selected" : ""}`}
              onClick={() => selectMember(member)}
            >
              <MemberPhoto member={member} />
              <span>
                <strong>{member.name}{member.profile_type === PROFILE_TYPE_AI_AGENT ? " · AI" : ""}</strong>
                <small>{member.job_title || (member.profile_type === PROFILE_TYPE_AI_AGENT ? "AI Engineer" : "No job title")} · {member.profile_type === PROFILE_TYPE_AI_AGENT ? agentModelLabel(member.agent_model) : member.level || DEFAULT_ENGINEER_LEVEL}</small>
              </span>
              <em>{member.profile_type === PROFILE_TYPE_AI_AGENT ? `${member.agent_max_concurrent_jobs} concurrent` : `${member.availability_hours_per_week}h/week`}</em>
            </button>
          ))}
        </section>
        <section className="team-editor">
          <div className="team-editor-heading">
            <div><span>{isNew ? "New profile" : "Team profile"}</span><h2>{draft.name || "Unnamed team member"}</h2></div>
            {draft.photo_data_url ? <img src={draft.photo_data_url} alt="" /> : <div className="team-photo-placeholder">{initials(draft.name)}</div>}
          </div>
          <label>Profile type<select value={draft.profile_type} onChange={(event) => updateDraft(setDraft, "profile_type", event.target.value)}><option value={PROFILE_TYPE_HUMAN}>Human</option><option value={PROFILE_TYPE_AI_AGENT}>AI Engineer</option></select></label>
          <label>Name<input value={draft.name} onChange={(event) => updateDraft(setDraft, "name", event.target.value)} /></label>
          <label>Job title<input value={draft.job_title} onChange={(event) => updateDraft(setDraft, "job_title", event.target.value)} /></label>
          {draft.profile_type === PROFILE_TYPE_HUMAN && <label>GitHub username<input aria-label="GitHub username" value={draft.github_username || ""} onChange={(event) => updateDraft(setDraft, "github_username", event.target.value)} placeholder="octocat" /><small>Used to request this engineer as a pull request reviewer.</small></label>}
          {draft.profile_type === PROFILE_TYPE_HUMAN && <label>Level<select value={draft.level} onChange={(event) => updateDraft(setDraft, "level", event.target.value)}>{ENGINEER_LEVELS.map((level) => <option key={level}>{level}</option>)}</select></label>}
          <label>Main skills<textarea rows="5" value={draft.main_skills.join("\n")} onChange={(event) => updateDraft(setDraft, "main_skills", lines(event.target.value))} placeholder="One skill per line" /></label>
          {draft.profile_type === PROFILE_TYPE_HUMAN ? (
            <label>Availability for this workspace (hours per week)<input type="number" min="0" max="168" value={draft.availability_hours_per_week} onChange={(event) => updateDraft(setDraft, "availability_hours_per_week", Number(event.target.value))} /></label>
          ) : (
            <div className="team-agent-settings">
              <h3>AI Engineer settings</h3>
              <label>Execution model<select value={draft.agent_model} onChange={(event) => updateDraft(setDraft, "agent_model", event.target.value)}>{AGENT_MODEL_OPTIONS.map((option) => <option key={option.id} value={option.id}>{option.label}</option>)}</select></label>
              <label>Reasoning effort<select value={draft.agent_reasoning_effort} onChange={(event) => updateDraft(setDraft, "agent_reasoning_effort", event.target.value)}>{AGENT_REASONING_EFFORT_OPTIONS.map((option) => <option key={option.id} value={option.id}>{option.label}</option>)}</select></label>
              <label>Maximum cost per run (USD)<input type="number" min="0.01" max="1000" step="0.01" value={draft.agent_max_cost_per_run} onChange={(event) => updateDraft(setDraft, "agent_max_cost_per_run", Number(event.target.value))} /></label>
              <label>Maximum concurrent jobs<input type="number" min="1" max="20" value={draft.agent_max_concurrent_jobs} onChange={(event) => updateDraft(setDraft, "agent_max_concurrent_jobs", Number(event.target.value))} /></label>
              <label>Execution instructions<textarea rows="6" value={draft.agent_instructions} onChange={(event) => updateDraft(setDraft, "agent_instructions", event.target.value)} placeholder="Repository conventions, preferred test strategy, implementation boundaries..." /></label>
            </div>
          )}
          <label>Photo<input type="file" accept="image/png,image/jpeg,image/webp" onChange={uploadPhoto} /></label>
          <div className="team-editor-actions">
            <button className="primary" onClick={saveMember} disabled={loading || (!isNew && !hasChanges)}>{loading ? "Saving..." : "Save profile"}</button>
            {!isNew && <button className="danger" onClick={deleteMember} disabled={loading}>Delete profile</button>}
          </div>
        </section>
      </div>
    </div>
  );
};

const MemberPhoto = ({ member }) => member.photo_data_url
  ? <img className="team-member-photo" src={member.photo_data_url} alt="" />
  : <span className="team-member-photo placeholder">{initials(member.name)}</span>;
const initials = (name = "") => name.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join("").toUpperCase() || "?";
const lines = (value) => value.split("\n");
const normalizeSkills = (skills) => skills.map((item) => item.trim()).filter(Boolean);
const updateDraft = (setter, field, value) => setter((current) => ({ ...current, [field]: value }));
const toDraft = (member) => ({
  name: member.name,
  job_title: member.job_title,
  github_username: member.github_username || "",
  level: member.level || DEFAULT_ENGINEER_LEVEL,
  main_skills: member.main_skills,
  availability_hours_per_week: member.availability_hours_per_week,
  photo_data_url: member.photo_data_url,
  profile_type: member.profile_type || PROFILE_TYPE_HUMAN,
  agent_model: member.agent_model || DEFAULT_AGENT_MODEL,
  agent_reasoning_effort: member.agent_reasoning_effort || DEFAULT_AGENT_REASONING_EFFORT,
  agent_max_cost_per_run: member.agent_max_cost_per_run ?? 5,
  agent_max_concurrent_jobs: member.agent_max_concurrent_jobs ?? 1,
  agent_instructions: member.agent_instructions || ""
});
const agentModelLabel = (model) => AGENT_MODEL_OPTIONS.find((option) => option.id === model)?.label || AGENT_MODEL_OPTIONS[0].label;

export default TeamStudioPage;
